import {
  CurecallButton,
  CurecallModalContainer,
  CurecallSelect,
  CurecallSelectItem,
  CurecallTextarea,
  ECurecallButtonAppearance,
  ECurecallNotificationAppearance,
} from '@honestica/curecall-ui'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useNotification } from 'src/providers/notification.provider'
import { PatientService } from 'src/services/patient.service'
import { EEndReason, TPatientFollowup } from 'src/types/followup'
import { TPatient } from 'src/types/patient'
import { getFullName } from 'src/utils/string-formatter.util'

export type Props = {
  onToggle: VoidFunction
  patient: TPatient
  currentPatientFollowup: TPatientFollowup
}

const EndFollowupModal = ({ onToggle, patient, currentPatientFollowup }: Props) => {
  const { t } = useTranslation()
  const { notify } = useNotification()
  const { project } = useCurrentProject()
  const queryClient = useQueryClient()

  const [endReason, setEndReason] = useState<CurecallSelectItem>()
  const [isLoading, setIsLoading] = useState(false)

  const followupName = currentPatientFollowup.Followup.name
  const patientName = getFullName(patient)
  const END_REASON_OPTIONS: { value: EEndReason; label: string }[] = [
    {
      value: EEndReason.PATIENT_DEATH,
      label: t('components.patient.patient-file.body.followup-details.end-followup.patient-death'),
    },
    {
      value: EEndReason.PATIENT_HOSPITALISED,
      label: t('components.patient.patient-file.body.followup-details.end-followup.patient-hospitalized'),
    },
    {
      value: EEndReason.OTHER,
      label: t('components.patient.patient-file.body.followup-details.end-followup.other'),
    },
  ]
  const methods = useForm({
    defaultValues: {
      endReason: undefined,
      stoppedReasonDetails: '',
    },
  })
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    clearErrors,
    watch,
  } = methods

  type TEndFollowupFormData = {
    endReason?: EEndReason
    stoppedReasonDetails?: string
  }

  const endPatientFollowup = async (data: TEndFollowupFormData) => {
    if (!endReason) return

    const isOther = endReason.value === EEndReason.OTHER
    const trimmedDetails = data.stoppedReasonDetails?.trim() || ''
    if (isOther && !trimmedDetails) {
      return
    }

    const payload = {
      endReason: endReason.value,
      ...(isOther && { stoppedReasonDetails: trimmedDetails }),
    }

    setIsLoading(true)
    try {
      const result = await PatientService.endFollowup({
        projectId: project.id,
        patientId: patient.id,
        patientFollowupId: currentPatientFollowup.id,
        data: payload,
      })
      notify(
        {
          title: t(
            'components.patient.patient-file.body.followup-details.end-followup.success-title-when-ending',
          ),
          message: t(
            'components.patient.patient-file.body.followup-details.end-followup.success-body-when-ending',
          ),
          appearance: ECurecallNotificationAppearance.SUCCESS,
        },
        15000,
      )
      if (queryClient.getQueryData(['patients', project.id, patient.id]) !== undefined && result?.data) {
        queryClient.setQueryData(['patients', project.id, patient.id], (oldData: any) => {
          const patientFollowups = oldData?.PatientFollowups
          if (!patientFollowups) return oldData
          const updatedPatientFollowups = patientFollowups.map((pf: TPatientFollowup) =>
            pf.id === currentPatientFollowup.id ? { ...pf, ...result?.data } : pf,
          )
          return { ...oldData, PatientFollowups: updatedPatientFollowups }
        })
      }
    } catch (e: any) {
      notify(
        {
          title: t('components.patient.patient-file.body.followup-details.end-followup.error-when-ending'),
          message: e,
          appearance: ECurecallNotificationAppearance.ERROR,
        },
        15000,
      )
    } finally {
      setIsLoading(false)
      onToggle()
    }
  }

  const onChangeEndReason = (newEndReason: CurecallSelectItem) => {
    setEndReason(newEndReason)
    setValue('endReason', newEndReason.value)
    clearErrors('endReason')
  }

  return (
    <CurecallModalContainer
      closeModalAriaLabel={t('components.medical-team-comments.close')}
      title={t('components.patient.patient-file.body.followup-details.end-followup.explainations', {
        followupName,
        patientName,
      })}
      isOpen={true}
      toggle={onToggle}
      dialogClassName="c-w-[900px]"
    >
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(endPatientFollowup)} autoComplete="off">
          <CurecallSelect
            {...register('endReason', {
              required: t(
                'components.patient.patient-file.body.followup-details.end-followup.other-required-error',
              ),
            })}
            label={t('components.patient.patient-file.body.followup-details.end-followup.end-reason')}
            items={END_REASON_OPTIONS}
            onChange={onChangeEndReason}
            selectedItem={endReason}
            placeholder={t(
              'components.patient.patient-file.body.followup-details.end-followup.end-field-placeholder',
            )}
            isRequired={true}
            errorMessage={errors?.endReason?.message}
            wrapperClassName="c-mb-10"
          />

          {endReason?.value === EEndReason.OTHER && (
            <CurecallTextarea
              {...register('stoppedReasonDetails', {
                validate: (value) =>
                  endReason?.value !== EEndReason.OTHER ||
                  (value && value.trim().length > 0) ||
                  t(
                    'components.patient.patient-file.body.followup-details.end-followup.other-required-error',
                  ),
              })}
              label={t('components.patient.patient-file.body.followup-details.end-followup.end-details')}
              placeholder={t(
                'components.patient.patient-file.body.followup-details.end-followup.end-details-placeholder',
              )}
              maxLength={250}
              isRequired={true}
              required={true}
              value={watch('stoppedReasonDetails')}
              onChange={(e) => {
                setValue('stoppedReasonDetails', e.target.value)
                clearErrors('stoppedReasonDetails')
              }}
              errorMessage={errors?.stoppedReasonDetails?.message}
            />
          )}

          <div className="c-mt-32 c-flex c-justify-center c-gap-4">
            <CurecallButton appearance={ECurecallButtonAppearance.CANCEL} onClick={onToggle} type="button">
              {t('components.modals.cancel')}
            </CurecallButton>
            <CurecallButton
              type="submit"
              appearance={ECurecallButtonAppearance.ALERT}
              isLoading={isLoading}
              disabled={isLoading}
            >
              {t('components.modals.confirm')}
            </CurecallButton>
          </div>
        </form>
      </FormProvider>
    </CurecallModalContainer>
  )
}

export { EndFollowupModal }
